// MT: sepet olaylarını sade CustomEvent olarak yayınlar (MASTER-PLAN-v2 §3.3, §7, H8) ve sepet ilerleme çubuğunu
// (snippets/mt-cart-extras 'top', spec/port-diger.md §1.7 + ortak arayüz spec/port-pdp.md §5) yönetir.
// Amaç: sepet / upsell uygulamaları temanın iç olay sistemini bilmeden dinleyebilsin ve sepeti tazeletebilsin.
//
// Yayınlanan (document üzerinde):
//   mt:cart:updated        detail { action, source, cart }  — satır, indirim ya da not değişti (istek bittikten sonra)
//   mt:cart:drawer-open    detail {}                         — tema çekmecesi açıldı
//   mt:cart:drawer-close   detail {}                         — tema çekmecesi kapandı
//   mt:volume:tier-change  detail { offerId, total, tierMin, pct, nextMin } — kademe YÜKSELDİ (yalnız artışta)
//   mt:volume:mismatch     detail { offerId, expected, got } — ulaşılan kademenin indirimi /cart.js satırında yok
//   mt:gift:sync           detail { reason }                 — paket değişti (başarılı ya da yarım); hediye eşitlemesi (mt-gift-cart.js)
// "X'liye geç" tek cart/update.js isteğidir (eski paket 0 + yeni paket); hata olursa çubuğun altında role="alert" uyarı.
// Dinlenen (uygulamalar ve tema gönderir):
//   mt:cart:refresh        — sepet başka yerden değişti; çekmece ve sepet sayfası yeniden çizilir
//   mt:cart:open / mt:cart:close — tema çekmecesini açar / kapatır
//   mt:volume:mismatch     — ürün sayfası bekçisi de yayınlar; aynı teklifin çubuğu gizlenir
// Tema çekmecesini tamamen kapatmak için: Tema ayarları → Sepet → Sepet türü = Sayfa.
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const SOURCE = 'mt-cart-events';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const LEVEL_KEY = 'mt-progress-level';

function emit(name, detail) {
  document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
}

function drawer() {
  return /** @type {(HTMLElement & {open?: () => void, close?: () => void}) | null} */ (
    document.querySelector('theme-drawer#cart-drawer')
  );
}

/** @param {Event & {action?: string, context?: string, promise?: Promise<any>}} event */
function relay(event) {
  const done = event.promise || Promise.resolve(null);
  done
    .then((result) => {
      if (result && result.detail && result.detail.didError) return;
      emit('mt:cart:updated', {
        action: event.action || event.type,
        source: event.context || null,
        cart: (result && result.cart) || null,
      });
    })
    .catch(() => {});
}

/** Sepeti çizen bölümleri (çekmece, sepet sayfası) Horizon'un kendi olayıyla tazeler. */
async function refresh() {
  const ids = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  let sections = {};
  if (ids.length) {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    sections = await fetch(url.toString()).then((r) => (r.ok ? r.json() : {}));
  }
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: SOURCE, lines: [], promise: deferred.promise }));
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { source: SOURCE, sections, itemCount: cart.item_count, items: cart.items },
  });
}

/**
 * Hata yanıtı da okunur: 422 (stok vb.) Shopify'ın açıklamasıyla fırlatılır (`error.description`, müşteriye gösterilir).
 * @param {string} path @param {object} body
 */
async function post(path, body) {
  const response = await fetch(root + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = /** @type {Error & {description?: string}} */ (new Error(path + ' ' + response.status));
    error.description = (data && typeof data.description === 'string' && data.description) || '';
    throw error;
  }
  return data;
}

/* ---------------- İlerleme çubuğu ---------------- */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/** @type {Set<string>} */
const warned = new Set();

function storedLevels() {
  try {
    return JSON.parse(sessionStorage.getItem(LEVEL_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/** @param {string} id @param {number} level */
function storeLevel(id, level) {
  try {
    const all = storedLevels();
    all[id] = level;
    sessionStorage.setItem(LEVEL_KEY, JSON.stringify(all));
  } catch {
    /* depolama kapalı: parıltı yalnız oynamaz */
  }
}

/** @param {HTMLElement} el */
function readOffer(el) {
  try {
    return JSON.parse(el.dataset.mtOffer || '');
  } catch {
    return null;
  }
}

/**
 * Uyumsuzluk: çubuk hemen gizlidir; uyarı ve olay 3 sn sonra çizim hâlâ aynıysa yazılır. Ürün formu ekledikten
 * sonra hediye satırı ayrı istekle geldiği için ilk çizimde hediye geçici olarak eksik görünür (ölçüldü 2026-10-22).
 * @param {HTMLElement} el @param {any} offer @param {number | null} expected @param {number | null} got
 */
function mismatch(el, offer, expected, got) {
  const signature = el.dataset.mtOffer || '';
  window.setTimeout(() => {
    if (!el.isConnected || (el.dataset.mtOffer || '') !== signature) return;
    const key = JSON.stringify([offer.id, offer.total, expected, got]);
    if (warned.has(key)) return;
    warned.add(key);
    // Bilinçli durum (kurulum/indirim eksik): tema güvenli yöne geçti, çubuk gizli. Hata değil, uyarı.
    console.warn('[mt-cart] teklif indirimi sepette uygulanmadı; ilerleme çubuğu gizlendi', { offer, expected, got });
    emit('mt:volume:mismatch', { offerId: offer.id, expected, got });
  }, 3000);
}

/**
 * Paket başına hediye sayısı. TEK KAYNAK (QA tur 2 G1): çubuğun `data-mt-gift-config`'i = ürünün hediye teklifi
 * (metafield `mt_theme.gift_offer` → snippets/mt-gift-offer; ürün sayfası kartları ve mt-gift-unlock da aynı veriyi okur).
 * Paket = o varyantı açan (stokta) hediye sayısı. Teklif yoksa null: çubuk gizli kalır (uydurma yok).
 * Eski biçim (dizi) de okunur.
 * @param {HTMLElement} el @param {any} offer @returns {((p: any) => number) | null}
 */
function giftCounter(el, offer) {
  if (!el.dataset.mtGiftConfig) return null;
  let data;
  try {
    data = JSON.parse(el.dataset.mtGiftConfig);
  } catch {
    return null;
  }
  const tiers = Array.isArray(data) ? data : data && Array.isArray(data.gifts) ? data.gifts : null;
  if (!tiers) return null;
  if (!Array.isArray(data) && data.product && String(data.product) !== String(offer.product)) return null;
  const valid = tiers.filter((t) => t && t.available !== false && Array.isArray(t.for));
  return (p) => valid.filter((t) => t.for.some((/** @type {any} */ id) => String(id) === String(p.variant))).length;
}

/**
 * Paket modu: hediye sayısı giftCounter'dan; veri yoksa ya da başka ürüne aitse çubuk gizli kalır.
 * @param {HTMLElement} el @param {any} offer
 */
function fillPackage(el, offer) {
  const counter = giftCounter(el, offer);
  const packs = Array.isArray(offer.packs) ? offer.packs.slice().sort((a, b) => a.size - b.size) : [];
  /** @param {any} p */
  const gifts = (p) => (counter ? counter(p) : 0);
  const max = packs.reduce((m, p) => Math.max(m, gifts(p)), 0);
  if (!counter || !max) {
    el.hidden = true;
    return null;
  }
  const current = packs.find((p) => p.size === offer.best);
  const expected = current ? gifts(current) : 0;
  if (offer.open < expected) {
    el.hidden = true;
    mismatch(el, offer, expected, offer.open);
    return null;
  }
  const next = packs.find((p) => p.size > offer.best && p.available && gifts(p) > expected);
  const data = el.dataset;
  const text = el.querySelector('.mt-progress__text');
  const top = el.querySelector('.mt-progress__top');
  if (text) {
    text.innerHTML = next
      ? String(data.tplNext || '').replace('[size]', String(next.size)).replace('[gifts]', String(gifts(next)))
      : String(data.tplAll || '');
  }
  top?.querySelector('[data-mt-upgrade]')?.remove();
  if (next && top) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary mt-progress__button';
    button.dataset.mtUpgrade = '';
    button.dataset.key = offer.key;
    button.dataset.variant = String(next.variant);
    button.dataset.quantity = String(offer.quantity || 1);
    button.textContent = String(data.tplUpgrade || '').replace('[size]', String(next.size));
    top.append(button);
  }
  const d = Math.min(offer.open / max, 1);
  const bar = el.querySelector('.mt-progress__bar');
  const fill = /** @type {HTMLElement | null} */ (el.querySelector('.mt-progress__fill'));
  bar?.setAttribute('aria-valuenow', String(Math.round(d * 100)));
  fill?.style.setProperty('--d', d.toFixed(3));
  bar?.querySelectorAll('.mt-progress__tick').forEach((t) => t.remove());
  const labels = el.querySelector('.mt-progress__labels');
  if (labels) labels.replaceChildren();
  const steps = Array.from(new Set(packs.map(gifts).filter((g) => g > 0))).sort((a, b) => a - b);
  steps.forEach((g) => {
    const pos = ((g / max) * 100).toFixed(2) + '%';
    if (g < max && bar) {
      const tick = document.createElement('span');
      tick.className = 'mt-progress__tick';
      tick.style.insetInlineStart = pos;
      bar.append(tick);
    }
    if (labels) {
      const label = document.createElement('span');
      label.className = 'mt-progress__label' + (offer.open >= g ? ' is-reached' : '');
      label.style.insetInlineStart = pos;
      label.textContent = String((g === 1 ? data.tplLabelOne : data.tplLabelOther) || '').replace('[n]', String(g));
      labels.append(label);
    }
  });
  el.hidden = false;
  return { tierMin: offer.best, pct: 0, nextMin: next ? next.size : null };
}

/** @param {HTMLElement} el */
function processProgress(el) {
  const offer = readOffer(el);
  if (!offer || !offer.id) return;
  // Aynı çizim ikinci kez işlenmez (kendi DOM değişikliklerimiz gözlemciyi yeniden tetikler). Horizon morph sunucu
  // HTML'ini uygularken bu özniteliği siler; o zaman yeniden doldurulur.
  const signature = el.dataset.mtOffer || '';
  if (el.dataset.mtSeen === signature) return;
  el.dataset.mtSeen = signature;
  if (el.hasAttribute('data-mt-mismatch')) {
    mismatch(el, offer, offer.expected ?? null, offer.got ?? offer.open ?? null);
    return;
  }
  let info;
  if (offer.mode === 'package') {
    info = fillPackage(el, offer);
    if (!info) return;
  } else {
    const tier = Array.isArray(offer.tiers) && offer.level > 0 ? offer.tiers[offer.level - 1] : null;
    info = { tierMin: tier ? tier[0] : null, pct: tier ? tier[1] : 0, nextMin: offer.next ? offer.next.min : null };
  }
  const level = Number(offer.level) || 0;
  const previous = storedLevels()[offer.id];
  storeLevel(offer.id, level);
  if (typeof previous === 'number' && level > previous) {
    // Eşik geçildi: tek seferlik parıltı (CSS yalnız prefers-reduced-motion: no-preference'ta oynatır) + duyuru
    const bar = el.querySelector('.mt-progress__bar');
    if (bar && !reduceMotion()) {
      bar.classList.remove('is-threshold');
      void (/** @type {HTMLElement} */ (bar).offsetWidth);
      bar.classList.add('is-threshold');
      window.setTimeout(() => bar.classList.remove('is-threshold'), 1000);
    }
    let status = el.querySelector('[data-mt-progress-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      status.setAttribute('data-mt-progress-status', '');
      el.append(status);
    }
    status.textContent = el.querySelector('.mt-progress__text')?.textContent?.trim() || '';
    emit('mt:volume:tier-change', { offerId: offer.id, total: offer.total, ...info });
  }
}

let scheduled = 0;
function scheduleProcess() {
  if (scheduled) return;
  scheduled = requestAnimationFrame(() => {
    scheduled = 0;
    document.querySelectorAll('[data-mt-progress]').forEach((el) => {
      if (el instanceof HTMLElement) processProgress(el);
    });
  });
}

/* ---------------- Düğmeler: "X'liye geç" / "+N ekle" ---------------- */

/** @param {any} item */
const isGiftLine = (item) => Boolean(item && item.properties && item.properties._mt_gift);

/**
 * Paket yükseltme (QA tur1 genel-6): eski paket satırı 0 + yeni paket TEK `cart/update.js` isteğinde (satır anahtarı ve
 * varyant kimliği aynı `updates` nesnesinde). Yanıttaki sepet doğrulanır; Shopify isteği reddeder ya da varyant anahtarını
 * uygulamazsa sıralı yola düşülür ve yarım kalan adım geri alınır. Sonuç: sepette ya eski paket ya yeni paket kalır;
 * ikisi birden ya da hiçbiri değil.
 * @param {string} key @param {string} variant @param {number} quantity
 */
async function upgradePackage(key, variant, quantity) {
  /** @type {any[] | null} */
  let items = null;
  try {
    const cart = await post('cart/update.js', { updates: { [key]: 0, [variant]: quantity } });
    items = Array.isArray(cart && cart.items) ? cart.items : null;
  } catch {
    items = null; // reddedildi (4xx): sepet değişmedi, sıralı yol denenir
  }
  if (items) {
    const added = items.find((i) => !isGiftLine(i) && String(i.variant_id) === String(variant));
    const oldStays = items.some((i) => i.key === key);
    if (added && !oldStays) return;
    if (added && oldStays) {
      await removeOrRollback(key, added.key);
      return;
    }
    if (!oldStays) {
      // Eski satır gitti, yeni gelmedi: yeni eklenir; eklenemezse eski paket geri konur (satır anahtarı "varyant:özet").
      try {
        await post('cart/add.js', { items: [{ id: Number(variant), quantity }] });
      } catch (error) {
        await post('cart/add.js', { items: [{ id: Number(key.split(':')[0]), quantity }] }).catch(() => {});
        throw error;
      }
      return;
    }
  }
  // Sıralı yol: önce yeni paket (eklenemezse sepet değişmez), sonra eski satır; çıkarılamazsa eklenen geri alınır.
  const result = await post('cart/add.js', { items: [{ id: Number(variant), quantity }] });
  const line = Array.isArray(result && result.items) ? result.items[0] : null;
  await removeOrRollback(key, line && line.key);
}

/** @param {string} oldKey @param {string | null | undefined} newKey */
async function removeOrRollback(oldKey, newKey) {
  try {
    await post('cart/change.js', { id: oldKey, quantity: 0 });
  } catch (error) {
    if (newKey) await post('cart/change.js', { id: newKey, quantity: 0 }).catch(() => {});
    throw error;
  }
}

/**
 * Hata metni çubuğun altında, role="alert" (ekran okuyucu hemen okur). Sepet yeniden çizilince (morph) kendiliğinden gider.
 * @param {string} offerId @param {string} message
 */
function showProgressError(offerId, message) {
  if (!offerId) return;
  document.querySelectorAll(`[data-mt-progress][data-mt-offer-id="${CSS.escape(offerId)}"]`).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    let alert = el.querySelector('[data-mt-progress-error]');
    if (!alert) {
      alert = document.createElement('p');
      alert.className = 'mt-progress__error';
      alert.setAttribute('role', 'alert');
      alert.setAttribute('data-mt-progress-error', '');
      el.append(alert);
    }
    alert.textContent = message || el.dataset.tplError || '';
  });
}

/** @param {MouseEvent} event */
async function onProgressClick(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-mt-upgrade], [data-mt-add]') : null;
  if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();
  const { key, variant } = button.dataset;
  const quantity = Number(button.dataset.quantity) || 1;
  const progress = /** @type {HTMLElement | null} */ (button.closest('[data-mt-progress]'));
  const offerId = (progress && progress.dataset.mtOfferId) || '';
  progress?.querySelector('[data-mt-progress-error]')?.remove();
  button.setAttribute('aria-busy', 'true');
  /** @type {any} */
  let failure = null;
  try {
    if (button.hasAttribute('data-mt-upgrade')) {
      await upgradePackage(String(key), String(variant), quantity);
    } else {
      await post('cart/change.js', { id: key, quantity });
    }
  } catch (error) {
    failure = error;
    console.warn('[mt-cart] sepet güncellenemedi', error);
  } finally {
    button.removeAttribute('aria-busy');
    // QA tur 2 G1: yükseltme hangi yoldan bitti (tek update.js, yedek add.js + change.js ya da geri alma) bitsin,
    // hediye kümesi sepetteki pakete göre yeniden kurulur (mt-gift-cart.js tam eşitleme).
    if (button.hasAttribute('data-mt-upgrade')) emit('mt:gift:sync', { reason: failure ? 'upgrade-failed' : 'upgrade' });
  }
  if (!failure) {
    emit('mt:cart:refresh');
    return;
  }
  // Hata: sepet yeniden okunup çizilir (geri alma olduysa da müşteri gerçek durumu görür), sonra uyarı yazılır.
  try {
    await refresh();
  } catch (error) {
    console.error('[mt-cart-events] sepet tazelenemedi', error);
  }
  window.setTimeout(() => showProgressError(offerId, (failure && failure.description) || ''), 60);
}

/* ---------------- Yeni eklenen satır vurgusu (animasyon K1) ---------------- */

/** @param {Event & {action?: string, lines?: Array<{merchandiseId?: string | number}>, promise?: Promise<any>}} event */
function highlightAdded(event) {
  if (event.action !== 'add' || !Array.isArray(event.lines) || !event.lines.length) return;
  const ids = event.lines.map((line) => String(line.merchandiseId || '').replace(/\D+/g, '')).filter(Boolean);
  if (!ids.length) return;
  (event.promise || Promise.resolve(null))
    .then((result) => {
      if (result && result.detail && result.detail.didError) return;
      window.setTimeout(() => {
        ids.forEach((id) => {
          document.querySelectorAll(`.cart-items__table-row[data-variant-id="${id}"]:not([data-mt-gift-row])`).forEach((row) => {
            row.classList.remove('is-new');
            void (/** @type {HTMLElement} */ (row).offsetWidth);
            row.classList.add('is-new');
            window.setTimeout(() => row.classList.remove('is-new'), 1400);
          });
        });
      }, 120);
    })
    .catch(() => {});
}

if (!window.MtCartEvents) {
  window.MtCartEvents = { refresh };

  document.addEventListener(StandardEvents.cartLinesUpdate, relay);
  document.addEventListener(StandardEvents.cartDiscountUpdate, relay);
  document.addEventListener(StandardEvents.cartNoteUpdate, relay);
  document.addEventListener(StandardEvents.cartLinesUpdate, highlightAdded);

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target instanceof Element && event.target.id === 'cart-drawer') emit('mt:cart:drawer-open');
  });
  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target instanceof Element && event.target.id === 'cart-drawer') emit('mt:cart:drawer-close');
  });

  document.addEventListener('mt:cart:refresh', () => {
    refresh().catch((error) => console.error('[mt-cart-events] sepet tazelenemedi', error));
  });
  document.addEventListener('mt:cart:open', () => {
    const d = drawer();
    if (d && d.open) d.open();
    else window.location.href = root + 'cart';
  });
  document.addEventListener('mt:cart:close', () => drawer()?.close?.());

  document.addEventListener('click', onProgressClick);

  // Ürün sayfası bekçisi aynı teklif için uyumsuzluk bildirirse çubuk gizlenir (port-pdp §5).
  document.addEventListener('mt:volume:mismatch', (event) => {
    const id = /** @type {CustomEvent} */ (event).detail?.offerId;
    if (!id) return;
    document.querySelectorAll(`[data-mt-progress][data-mt-offer-id="${CSS.escape(String(id))}"]`).forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = true;
    });
  });

  // Çubuk sunucuda çizilir; Horizon her sepet güncellemesinde çekmeceyi/sayfayı morph ile yeniler.
  new MutationObserver(scheduleProcess).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-mt-offer'],
  });
  scheduleProcess();
}
